import { AppointmentData, InvoiceDocumentType, InvoiceState } from '../../../models';
import { TK } from '../../../store/translations/translationKeys';
import { isPaid } from '../../../shared/models-helpers/appointmentDataHelper';
import { Translate } from 'src/store/translations/hooks';

export interface FilterInstance {
    type: string;
    values: string[];
}

// Single-char codes keep the URL short
const TYPE_CODE: Record<string, string> = {
    appointmentsWith:              '0',
    clientsWith:                   '1',
    appointmentsDone:              '2',
    appointmentsNotDone:           '3',
    appointmentsConfirmedByClient:    '4',
    appointmentsNotConfirmedByClient: 'f',
    appointmentsRecurring:         '5',
    appointmentsNotRecurring:      '6',
    appointmentsPaied:              '7',
    appointmentsNotPaied:           'g',
    appointmentsFree:              '8',
    appointmentsNotFree:           '9',
    appointmentsPaidWithPack:      'a',
    appointmentsWithInvoice:       'b',
    appointmentsWithoutInvoice:    'c',
    appointmentsWithReceipt:       'd',
    appointmentsWithoutReceipt:    'e',
};
const CODE_TYPE = Object.fromEntries(Object.entries(TYPE_CODE).map(([k, v]) => [v, k]));

// Format: <code>[:<val1>[~<val2>...]][|<code>...]  invert prefix: !
const FILTER_SEP = '|';
const VALUE_SEP = '~';

export function serializeFilters(filters: FilterInstance[], invert: boolean): string {
    const parts = filters.map(f => {
        const code = TYPE_CODE[f.type] ?? f.type;
        if (f.values.length === 0) return code;
        return `${code}:${f.values.map(encodeURIComponent).join(VALUE_SEP)}`;
    });
    const encoded = parts.join(FILTER_SEP);
    return invert ? `!${encoded}` : encoded;
}

export function deserializeFilters(raw: string): { filters: FilterInstance[]; invert: boolean } {
    const invert = raw.startsWith('!');
    const body = invert ? raw.slice(1) : raw;
    const filters = body.split(FILTER_SEP).filter(Boolean).map(part => {
        const colonIdx = part.indexOf(':');
        if (colonIdx === -1) return { type: CODE_TYPE[part] ?? part, values: [] };
        const code = part.slice(0, colonIdx);
        const values = part.slice(colonIdx + 1).split(VALUE_SEP).map(decodeURIComponent);
        return { type: CODE_TYPE[code] ?? code, values };
    });
    return { filters, invert };
}

export interface FilterContext {
    t: Translate<TK>;
    branches: string[];
    types: string[];
    professionals: string[];
    clientNames: string[];
    serviceNameFn: (type: string) => string;
}

export interface FilterDefinition {
    type: string;
    labelKey: TK;
    hasValues?: boolean;
    getOptions?: (ctx: FilterContext) => string[];
    /** Pre-resolves any cross-appointment state before the filter loop. */
    resolve?: (values: string[], allAppointments: AppointmentData[], ctx: FilterContext) => (a: AppointmentData) => boolean;
    predicate?: (appointment: AppointmentData, values: string[], ctx: FilterContext) => boolean;
}

const invoiceTypes = [InvoiceDocumentType.Invoice, InvoiceDocumentType.SimplifiedInvoice, InvoiceDocumentType.InvoiceReceipt];
const receiptTypes = [InvoiceDocumentType.Receipt, InvoiceDocumentType.InvoiceReceipt];

const hasInvoice = (a: AppointmentData) => a.invoices?.some(inv => invoiceTypes.includes(inv.type) && inv.state == InvoiceState.Issued) ?? false;
const hasReceipt = (a: AppointmentData) => a.invoices?.some(inv => receiptTypes.includes(inv.type) && inv.state == InvoiceState.Issued) ?? false;

export const basicFilters: FilterDefinition[] = [
    // ── Status filters ───────────────────────────────────────────────────────
    { type: 'appointmentsDone',              labelKey: TK.appointmentsDone,              predicate: a => a.hasHappened === true },
    { type: 'appointmentsNotDone',           labelKey: TK.appointmentsNotDone,           predicate: a => !a.hasHappened },
    { type: 'appointmentsConfirmedByClient',    labelKey: TK.appointmentsConfirmedByClient,    predicate: (a) => !!a.confirmedByClientAt },
    { type: 'appointmentsNotConfirmedByClient', labelKey: TK.appointmentsNotConfirmedByClient, predicate: (a) => !a.confirmedByClientAt },
    { type: 'appointmentsRecurring',         labelKey: TK.appointmentsRecurring,         predicate: a => !!a.recurring },
    { type: 'appointmentsNotRecurring',      labelKey: TK.appointmentsNotRecurring,      predicate: a => !a.recurring },

    // ── Payment filters ──────────────────────────────────────────────────────
    { type: 'appointmentsPaied',    labelKey: TK.appointmentsPaied,    predicate: a => !!isPaid(a) },
    { type: 'appointmentsNotPaied', labelKey: TK.appointmentsNotPaied, predicate: a => !isPaid(a) },
    { type: 'appointmentsFree',              labelKey: TK.appointmentsFree,              predicate: a => a.price === 0 && !a.packId },
    { type: 'appointmentsNotFree',           labelKey: TK.appointmentsNotFree,           predicate: a => a.price > 0 },
    { type: 'appointmentsPaidWithPack',      labelKey: TK.appointmentsPaidWithPack,      predicate: a => !!a.packId },

    // ── Document filters ─────────────────────────────────────────────────────
    { type: 'appointmentsWithInvoice',       labelKey: TK.appointmentsWithInvoice,       predicate: a => !a.packId && hasInvoice(a) },
    { type: 'appointmentsWithoutInvoice',    labelKey: TK.appointmentsWithoutInvoice,    predicate: a => !a.packId && !hasInvoice(a) },
    { type: 'appointmentsWithReceipt',       labelKey: TK.appointmentsWithReceipt,       predicate: a => !a.packId && hasReceipt(a) },
    { type: 'appointmentsWithoutReceipt',    labelKey: TK.appointmentsWithoutReceipt,    predicate: a => !a.packId && !hasReceipt(a) },
];

export const filterDefinitions: FilterDefinition[] = [
    // ── Value-based filters ──────────────────────────────────────────────────
    {
        type: 'appointmentsWith',
        labelKey: TK.appointmentsWith,
        hasValues: true,
        getOptions: ctx => [
            ...ctx.branches,
            ...ctx.types,
            ...ctx.professionals,
            ...ctx.clientNames,
            ...basicFilters.map(f => ctx.t(f.labelKey)),
        ],
        predicate: (a, values, ctx) => {
            const basicFiltersLabels = basicFilters.map(f => ctx.t(f.labelKey))

            const data = [a.branchId, ctx.serviceNameFn(a.type), a.professionalName ?? '', a.clientName ?? ''];
            if (!values.filter(v => !basicFiltersLabels.includes(v ?? '')).every(v => data.includes(v))) return false;

            const basicFiltersApplied = values.filter(v => basicFiltersLabels.includes(v ?? ''))
            if (basicFiltersApplied.length === 0) return true;

            return basicFiltersApplied.every(v => {
                const filterDef = basicFilters.find(f => ctx.t(f.labelKey) === v)
                if (!filterDef || !filterDef.predicate) return true;
                return filterDef.predicate!(a, [], ctx);
            })
        },
    },
    {
        type: 'clientsWith',
        labelKey: TK.clientsWithAppointmentsOf,
        hasValues: true,
        getOptions: ctx => [...ctx.professionals, ...ctx.types],
        resolve: (values, allAppointments, ctx) => {
            const eligibleClients = new Set(
                allAppointments
                    .filter(a => values.every(v => [ctx.serviceNameFn(a.type), a.professionalName ?? ''].includes(v)))
                    .map(a => a.clientName ?? '')
            );
            return a => !!a.clientName && eligibleClients.has(a.clientName);
        },
    },

    ...basicFilters,
];
